import {Box, LoadingOverlay, TextInput} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import {BasicButton} from '@packages/ui';
import {useState} from 'react';
import type {z} from 'zod';
import {STUDENT_CARD_APPLIED} from '../../../config/constants/routes';
import isAppWebView from '../../../config/isAppWebView';
import {PostStudentCardSchema} from '../../../core/entities/register/schema';
import {
  useCheckPrivateEmailExist,
  usePostStudentCard,
} from '../../../core/repositories/registerRepository';
import {useReCaptcha} from '../../../core/usecases/useReCaptcha';
import type {NextPageWithLayout} from '../../../pages/_app';
import type {Props} from '../../../pages/student_card';
import {CommonLink} from '../../../shared/components/CommonLink';
import {SectionTitle} from '../../../shared/components/SectionTitle';
import {showErrorSnackBar} from '../../../shared/components/SnackBar';

export const StudentCardPage: NextPageWithLayout<Props> = () => {
  const form = useForm<z.infer<typeof PostStudentCardSchema>>({
    validate: zodResolver(PostStudentCardSchema),
    initialValues: {
      firstName: '',
      familyName: '',
      email: '',
      registerType: null,
      images: [
        {
          imageBase64: '',
          imageFileName: '',
          side: false,
        },
        {
          imageBase64: '',
          imageFileName: '',
          side: true,
        },
      ],
    },
  });

  const onChangeHandler = async (img: File) => {
    const reader = new FileReader();
    if (['image/heic', 'image/heif'].includes(img.type)) {
      const heic2any = (await import('heic2any')).default;
      const imgBlob = new Blob([img], {type: img.type});
      const heicResult = (await heic2any({
        blob: imgBlob,
        toType: 'image/jpeg',
        quality: 1,
      })) as Blob;
      reader.readAsDataURL(heicResult);
    } else {
      reader.readAsDataURL(img);
    }

    reader.addEventListener('load', async () => {
      if (!reader.result) return;
      const imageIndex = isFrontImage ? 0 : 1;
      form.setFieldValue(
        `images.${imageIndex}.imageBase64`,
        reader.result as string,
      );
      form.setFieldValue(
        `images.${imageIndex}.imageFileName`,
        `${window.btoa(encodeURIComponent(img.name)).slice(0, 200)}.${img.name.split('.').pop()}` ||
          '',
      );
      setIsFrontImage(!isFrontImage);
    });
  };

  const [isPrivateEmailExist, setIsPrivateEmailExist] = useState(false);
  const handleCheckPrivateEmailExist = async () => {
    await refetch();

    if (checkPrivateEmailExist?.ok && checkPrivateEmailExist.value.exists) {
      setIsPrivateEmailExist(true);
    } else {
      setIsPrivateEmailExist(false);
    }
  };

  const {data: checkPrivateEmailExist, refetch} = useCheckPrivateEmailExist({
    queryParams: {
      email: form.values.email,
    },
    config: {
      enabled: /.+@.+\..+/.test(form.values.email),
    },
  });

  const [isLoading, handlers] = useDisclosure(false);
  const postStudentCard = usePostStudentCard({
    onSuccessFunc() {
      handlers.close();
      location.href = `${STUDENT_CARD_APPLIED}?email=${encodeURIComponent(form.values.email)}`;
    },
    onErrorFunc() {
      handlers.close();
    },
  });
  const {reCaptcha} = useReCaptcha();
  const onSubmit = () => {
    const result = form.validate();
    if (result.hasErrors) return;

    if (isAppWebView()) {
      postStudentCard.mutate({
        requestBody: {
          ...form.values,
        },
      });
    } else {
      reCaptcha('submit')
        .then(() => {
          handlers.open();
          postStudentCard.mutate({
            requestBody: {
              ...form.values,
            },
          });
        })
        .catch(() => {
          showErrorSnackBar({
            isNetworkError: false,
            title: 'reCAPTCHAエラー',
            message: '再度お試しください',
          });
        });
    }
  };

  const [isFrontImage, setIsFrontImage] = useState<boolean>(true);

  return (
    <Box className="w-full max-w-[664px]">
      <SectionTitle order={2} title={'会員登録'} />
      <Box className="relative mb-16 bg-white px-16 py-16 pc:px-32">
        <LoadingOverlay visible={isLoading} />
        <Box className="py-16 text-base font-bold">
          <span className="text-notification">
            海外の大学に在籍されている方は、
          </span>
          <CommonLink
            href="/inquiry/foreign_univ"
            className="hover:underline hover:decoration-primary-2 text-primary-2"
          >
            こちらから
          </CommonLink>
          <span className="text-notification">登録手続きを行って下さい。</span>
        </Box>
        <Box className="text-lg font-bold">学生証アップロード</Box>
        <p className="text-base p-8 px-16">
          所属先の学生証の写真をアップロードしてください。
        </p>
        <Box className="text-base py-8">
          <Box className="p-8 font-bold">※ご注意※</Box>
          <Box className="my-4">
            1)
            四年制大学・大学院・高等専門学校に在籍され、学士と同等以上の学位を取得できる方のみご登録いただけます。それ以外の方はご登録できませんので何卒ご了承ください。
          </Box>
          <Box className="my-4">
            2)
            学生証による会員登録は、画像審査のため申請から最大1週間程度かかる場合がございます。また画像が不鮮明である場合などは、登録をお受けいたしかねますのでご了承ください。
          </Box>
        </Box>
        <Box className="border border-black h-[300px]">
          <Box className="h-[150px] flex justify-around my-24">
            {form.values.images.map((image, index) => {
              return image.imageBase64 ? (
                <img
                  key={index}
                  src={form.values.images[index]?.imageBase64}
                  alt={index === 0 ? '表面' : '裏面'}
                  width={150}
                  height={150}
                />
              ) : (
                <Box
                  key={index}
                  className="border border-black w-[150px] text-center"
                >
                  <p className="leading-[150px]">
                    {index === 0 ? '表面' : '裏面'}
                  </p>
                </Box>
              );
            })}
          </Box>

          <Box className="flex justify-around">
            <label className="bg-black-1 text-white rounded-[16px] py-12 px-24 cursor-pointer font-bold">
              {isFrontImage
                ? '表面の写真をアップロード (1/2)'
                : '裏面の写真をアップロード (2/2)'}
              <input
                type="file"
                hidden
                name={isFrontImage ? 'frontImage' : 'backImage'}
                accept="image/*"
                onChange={async (e) => {
                  if (
                    !e.currentTarget ||
                    !e.currentTarget.files ||
                    e.currentTarget.files.length === 0 ||
                    e.currentTarget.files[0] === undefined ||
                    typeof window === 'undefined'
                  ) {
                    return;
                  }

                  await onChangeHandler(e.currentTarget.files[0]);
                }}
                maxLength={20_000_000}
              />
            </label>
          </Box>
          {form.getInputProps('images.0.imageBase64').error && (
            <Box className="text-notification-1 text-center">
              {'表面の写真をアップロードしてください'}
            </Box>
          )}
          {form.getInputProps('images.1.imageBase64').error && (
            <Box className="text-notification-1 text-center">
              {'裏面の写真をアップロードしてください'}
            </Box>
          )}
        </Box>

        <p className="text-base p-8">
          取り込んだ画像に、顔写真、お名前、所属学校名、有効期限または発行日が分かるように表示されているかご確認ください。{' '}
        </p>

        <Box className="py-16">
          <span className="text-base font-bold">氏名</span>
          <Box className="flex justify-around mx-16">
            <TextInput
              label="姓"
              className="w-1/2 mx-16"
              {...form.getInputProps('familyName')}
            />
            <TextInput
              label="名"
              className="w-1/2 mx-16"
              {...form.getInputProps('firstName')}
            />
          </Box>
        </Box>

        <span className="text-base font-bold">連絡先メールアドレス</span>
        <Box>
          <TextInput
            className="py-4 mx-32"
            {...form.getInputProps('email')}
            onBlur={async () => {
              await handleCheckPrivateEmailExist();
            }}
          />
        </Box>
        {!form.errors.privateEmail && isPrivateEmailExist && (
          <p className="text-notification text-base">
            すでに登録済みのメールアドレスです
          </p>
        )}
        <Box className="text-base p-16">
          <p>
            入力されたメールアドレスは会員登録後のログインなどに使用します。よく使うメールアドレスをご入力ください。
          </p>
          <p className="text-notification">
            携帯キャリアのメールアドレスはご利用いただけません。
          </p>
          <p className="text-notification">
            Microsoftのメールアドレス（msn、hotmail、outlook、live）はご利用いただけません。
          </p>
          <p>PCで閲覧可能なメールアドレス（例:gmail）をご入力ください。</p>
          <p className="text-notification">
            学生証経由で会員登録いただいた方は、友達紹介キャンペーンの対象外となります。
          </p>
          <p>予めご了承のほどお願い致します。</p>
        </Box>

        <Box className="flex justify-center px-16 py-8">
          <BasicButton
            type="submit"
            label="申請する"
            onClick={() => {
              onSubmit();
            }}
          />
        </Box>
        <p className="text-sm p-16">
          ※アップロードしていただいた画像は弊社が責任をもって管理し、会員登録以外には一切使用いたしません。
        </p>
      </Box>
    </Box>
  );
};
